/* eslint-disable import/no-anonymous-default-export */
import { account } from "helpers/account";
import {
  errorResponse,
  makePOSTRequest,
  handleResponse,
  makeDELETERequest,
} from "utils/apiRequests";
import { parse, format, addDays, min, differenceInDays } from 'date-fns';
import { apiUrl } from "../../config";


/**
 * Convert "06/07/2025" → Date
 */
const toDateObj = (dateString: string) => parse(dateString, 'dd/MM/yyyy', new Date());

/**
 * Convert Date → "06/07/2025"
 */
const toString = (date) => format(date, 'dd/MM/yyyy');

const fetchProfessionalAvailability = ({
  practiceId,
  availabilityRequests,
  fromDate,
}) => {
  const url = apiUrl(account)(`/practices/${practiceId}/vacancies`);

  return makePOSTRequest(url)({
    onlineBookingVacancyForm: { practiceId, availabilityRequests, fromDate },
  })
    .then(handleResponse)
    .catch(errorResponse);
};

export const CACHE = new Map();

async function fetchAvailabilityBetweenRange({
  practiceId,
  availabilityRequests,
  fromDate,
  toDate,
}) {
  const url = apiUrl(account)(
    `/practices/${practiceId}/vacancies/search_availability`
  );

  // Include availabilityRequests in cache key so different service/practitioner combos get fresh data
  const requestsKey = availabilityRequests ? JSON.stringify(availabilityRequests) : '';
  const CACHE_KEY = `${practiceId}-${fromDate}-${toDate}-${requestsKey}`;
  if (CACHE.has(CACHE_KEY)) {
    const cachedResponse = CACHE.get(CACHE_KEY);
    if (cachedResponse.expiry > Date.now()) {
      return cachedResponse;
    }
    CACHE.delete(CACHE_KEY);
  }
  
  let results = [];
  const BATCH_SIZE = 10; // 10 day batches
  
  const start = toDateObj(fromDate);
  const end   = toDateObj(toDate);
  const batchSize = differenceInDays(end, start) > BATCH_SIZE ? BATCH_SIZE : differenceInDays(start, end); // 14 days max
  let batchStart = start;

  while (batchStart < end) {
    const batchEnd = differenceInDays(end, batchStart) > batchSize ? min([addDays(batchStart, batchSize), end]) : end;
    
    const batch = await makePOSTRequest(url)({ onlineBookingVacancyForm: { practiceId, availabilityRequests, fromDate: toString(batchStart), toDate: toString(batchEnd) } }).then(handleResponse).catch(errorResponse);
    results.push(...batch.response);
    results = [...results, ...batch.response];
    batchStart = batchEnd;
  }
  const response = { response: results };
  CACHE.set(CACHE_KEY, { ...response, expiry: Date.now() + 1000 * 60 * 2 }); // Cache for 2 minutes
  return response;
};

const reservation = ({ practiceId, requestItem }) => {
  const url = apiUrl(account)(
    `/practices/${practiceId}/appointment_reservations`
  );
  return makePOSTRequest(url)({
    onlineBookingReservationForm: { ...requestItem },
  })
    .then(handleResponse)
    .catch(errorResponse);
};

const deleteReservation = ({ practiceId, requestItem }) => {
  const { reservationKey } = requestItem;
  if (!reservation) return;
  const url = apiUrl(account)(
    `/practices/${practiceId}/appointment_reservations/${reservationKey}`
  );
  return makeDELETERequest(url).then(handleResponse).catch(errorResponse);
};

export default {
  fetchProfessionalAvailability,
  fetchAvailabilityBetweenRange,
  reservation,
  deleteReservation,
};
